
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import BaseCrudRepository from '@/repositories/BaseCrudRepository';
import { combineLatest, ReplaySubject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import EmbeddedForm from '@/util/form/EmbeddedForm';
import FormModel from '@/repositories/data/FormModel';
import GlobalState from '@/util/GlobalState';
import ValidationErrorResponse from '@/repositories/data/ValidationErrorResponse';
import ProductRepository from '@/repositories/webshop/ProductRepository';
import ProductInventoryState from '@/repositories/data/webshop/ProductInventoryState';

@Component
export default class ProductInventoryFormModel extends Vue {
    @Inject()
    private readonly globalState!: GlobalState;

    @Prop({ type: Object, required: true })
    private readonly embed!: EmbeddedForm;

    @Prop({ type: Object, required: true })
    private readonly parentEntityRepository!: ProductRepository;

    @Prop({ type: Number, required: true })
    private readonly parentId!: number;

    private loading = true;
    private items: any[] = [];
    private formModel: FormModel | null = null;
    private formDialog = false;
    private formLoading = false;
    private deleteDialog = false;
    private deleteTarget: any = null;
    private deleting = false;
    private deleteSubscription = new Subscription();
    private parentRepositorySubject = new ReplaySubject<BaseCrudRepository<any, any, any>>(1);
    private parentIdSubject = new ReplaySubject<number>(1);
    private relationshipSubject = new ReplaySubject<string>(1);
    private listingSubscription = new Subscription();
    private formModelSubscription = new Subscription();
    private inventoryState: ProductInventoryState | any[] = [];
    private isNew: boolean = true;

    public created(): void {
        this.isNew = !this.$route.params.hasOwnProperty('id');
        this.listingSubscription = combineLatest([
            this.parentRepositorySubject,
            this.parentIdSubject,
            this.relationshipSubject,
        ])
            .pipe(switchMap(([repository, id, relationship]) => repository.findRelated(id, relationship)))
            .subscribe((relatedResource) => {
                if (relatedResource.isSuccess) {
                    this.items = relatedResource.data ? relatedResource.data : [];
                }
                this.loading = relatedResource.isLoading;
                if (relatedResource.isError) {
                    this.$emit('error', relatedResource.error);
                }
            });

        this.parentEntityRepository.getStatus(this.parentId).subscribe((resource) => {
            if (resource.isSuccess) {
                this.inventoryState = resource.data ? resource.data : [];
            }
        });
    }

    public beforeDestroy(): void {
        this.deleteSubscription.unsubscribe();
        this.listingSubscription.unsubscribe();
        this.formModelSubscription.unsubscribe();
    }

    public onNew(): void {
        this.formModelSubscription.unsubscribe();
        this.formModel = null;
        this.formDialog = true;
        this.formModelSubscription = this.embed.entityRepository.introspect().subscribe((formModelResource) => {
            if (formModelResource.isSuccess) {
                this.formModel = formModelResource.data;
            }
            if (formModelResource.isError) {
                this.$emit('error', formModelResource.error);
            }
            this.formLoading = formModelResource.isLoading;
        });
    }

    public cancelForm(): void {
        this.formModelSubscription.unsubscribe();
        this.formModel = null;
        this.formDialog = false;
    }

    public onSubmit(): void {
        if (this.formModel !== null) {
            const model = this.formModel.extractData<any>();
            const isNew = this.formModel.isNew;
            model[this.embed.parentRefKey] = this.parentId;
            this.formModel.clearValidationState();
            this.formModelSubscription.unsubscribe();
            this.formModelSubscription = (
                isNew ? this.embed.entityRepository.create(model) : this.embed.entityRepository.update(model)
            ).subscribe((resource) => {
                if (resource.isSuccess) {
                    this.formModel = null;
                    this.formDialog = false;
                    this.relationshipSubject.next(this.embed.relationship);
                    this.globalState.toast(
                        this.$t('crud.' + this.i18nGroup + (isNew ? '.ui.created' : '.ui.updated')).toString(),
                    );
                } else if (resource.isError) {
                    let validationError = false;
                    const error = resource.error;
                    if (
                        error.response &&
                        error.response.status === 400 &&
                        error.response.data &&
                        error.response.data.data &&
                        error.response.data.data.validationError
                    ) {
                        const validationErrorResponse: ValidationErrorResponse = error.response.data.data;
                        validationError = true;
                        if (this.formModel) {
                            this.formModel.processValidationErrorResponse(validationErrorResponse);
                        }
                    }
                    if (validationError) {
                        this.globalState.toast(this.$t('general.validationError').toString());
                    } else {
                        this.$emit('error', resource.error);
                    }
                }
            });
        }
    }

    private deleteItem(item: any): void {
        this.deleteTarget = item;
        this.deleteDialog = true;
    }

    private confirmDelete(): void {
        if (this.deleteTarget) {
            this.deleteSubscription.unsubscribe();
            this.deleting = true;
            this.deleteSubscription = this.embed.entityRepository.delete(this.deleteTarget.id).subscribe((resource) => {
                this.deleting = resource.isLoading;
                this.deleteDialog = false;
                this.relationshipSubject.next(this.embed.relationship);
                if (resource.isError) {
                    this.$emit('error', resource.error);
                }
            });
        }
    }

    private cancelDelete(): void {
        this.deleteSubscription.unsubscribe();
        this.deleting = false;
        this.deleteDialog = false;
        this.deleteTarget = null;
    }

    private onError(error: any) {
        this.$emit('error', error);
    }

    @Watch('parentEntityRepository', { immediate: true })
    private onParentEntityRepositoryChanged(repository: BaseCrudRepository<any, any, any>): void {
        this.parentRepositorySubject.next(repository);
    }

    @Watch('parentId', { immediate: true })
    private onParentIdChanged(id: number): void {
        this.parentIdSubject.next(id);
    }

    @Watch('items', { immediate: true })
    private onItemsChanged(): void {
        this.parentEntityRepository.getStatus(this.parentId).subscribe((resource) => {
            if (resource.isSuccess) {
                this.inventoryState = resource.data ? resource.data : [];
            }
        });
    }

    @Watch('embed', { immediate: true })
    private onEmbedChanged(embed: EmbeddedForm): void {
        this.relationshipSubject.next(embed.relationship);
    }

    @Watch('$route')
    public onRouteChanged(): void {
        this.isNew = !this.$route.params.hasOwnProperty('id');
    }

    private get i18nGroup(): string {
        return this.embed.i18nGroup;
    }

    private get columns(): string[] {
        return this.embed.columns;
    }
}
